import { JSONTreeParser } from '../services/JSONTreeParser.js';

/**
 * JSONTreeViewer component for displaying JSON data in a collapsible tree structure
 */
export class JSONTreeViewer {
  constructor(container) {
    this.container = container;
    this.treeData = null;
    this.selectedNode = null;
    this.focusedNode = null;
    this.keyboardNavigationEnabled = true;
    this.isLoading = false;
    this.loadingTimeout = null;

    this.init();
    this.setupKeyboardNavigation();
  }

  /**
   * Initialize the tree viewer
   */
  init() {
    this.container.className = 'json-tree-viewer';
    this.container.innerHTML = '<div class="tree-placeholder">No JSON data to display</div>';
  }

  /**
   * Show loading state
   */
  showLoading(message = 'Processing JSON data...') {
    this.isLoading = true;
    this.container.innerHTML = `
      <div class="tree-loading">
        <div class="loading-spinner"></div>
        <div class="loading-message">${this.escapeHtml(message)}</div>
      </div>
    `;
    this.container.classList.add('tree-loading-state');
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    this.isLoading = false;
    this.container.classList.remove('tree-loading-state');
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  /**
   * Render JSON data as a tree structure
   * @param {any} jsonData - The parsed JSON data to display
   * @param {boolean} showLoadingForLargeData - Whether to show loading for large datasets
   */
  render(jsonData, showLoadingForLargeData = true) {
    // Hide any existing loading state
    this.hideLoading();

    if (!jsonData && jsonData !== 0 && jsonData !== false && jsonData !== null) {
      this.container.innerHTML = '<div class="tree-placeholder">No JSON data to display</div>';
      return;
    }

    try {
      // Show loading for potentially large datasets
      const jsonString = JSON.stringify(jsonData);
      const isLargeData = jsonString.length > 50000; // 50KB threshold

      if (showLoadingForLargeData && isLargeData) {
        this.showLoading('Building tree view...');

        // Use setTimeout to allow UI to update before processing
        this.loadingTimeout = setTimeout(() => {
          this.processAndRenderData(jsonData);
        }, 10);
      } else {
        this.processAndRenderData(jsonData);
      }
    } catch (error) {
      this.hideLoading();
      this.container.innerHTML = `<div class="tree-error">Error rendering tree: ${error.message}</div>`;
    }
  }

  /**
   * Process and render the JSON data
   * @param {any} jsonData - The parsed JSON data to display
   */
  async processAndRenderData(jsonData) {
    try {
      // Check if we should use async parsing for large datasets
      const jsonString = JSON.stringify(jsonData);
      const isVeryLargeData = jsonString.length > 100000; // 100KB threshold for async parsing

      if (isVeryLargeData) {
        // Show progress bar for very large datasets
        this.showLoadingWithProgress('Processing large dataset...');

        this.treeData = await JSONTreeParser.parseAsync(jsonData, 'root', (progress) => {
          this.updateProgress(progress);
        });
      } else {
        this.treeData = JSONTreeParser.parse(jsonData);
      }

      this.hideLoading();
      this.container.innerHTML = '';
      this.renderNode(this.treeData, this.container);
    } catch (error) {
      this.hideLoading();
      this.container.innerHTML = `<div class="tree-error">Error rendering tree: ${error.message}</div>`;
    }
  }

  /**
   * Show loading state with progress bar
   * @param {string} message - Loading message
   */
  showLoadingWithProgress(message = 'Processing JSON data...') {
    this.isLoading = true;
    this.container.innerHTML = `
      <div class="tree-loading">
        <div class="loading-spinner"></div>
        <div class="loading-message">${this.escapeHtml(message)}</div>
        <div class="tree-progress-container">
          <div class="tree-progress-bar" style="width: 0%"></div>
        </div>
      </div>
    `;
    this.container.classList.add('tree-loading-state');
  }

  /**
   * Update progress bar
   * @param {number} progress - Progress percentage (0-100)
   */
  updateProgress(progress) {
    const progressBar = this.container.querySelector('.tree-progress-bar');
    if (progressBar) {
      progressBar.style.width = `${Math.min(100, Math.max(0, progress))}%`;
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render a single tree node and its children
   * @param {TreeNode} node - The node to render
   * @param {HTMLElement} parentElement - The parent DOM element
   */
  renderNode(node, parentElement) {
    const nodeElement = document.createElement('div');
    nodeElement.className = 'tree-node';
    nodeElement.dataset.level = node.level;
    nodeElement.dataset.type = node.type;
    nodeElement.dataset.path = node.getPathString();

    // Create the node content
    const nodeContent = document.createElement('div');
    nodeContent.className = 'tree-node-content';

    // Add indentation based on level
    const indent = document.createElement('span');
    indent.className = 'tree-indent';
    indent.style.width = `${node.level * 20}px`;
    nodeContent.appendChild(indent);

    // Add expand/collapse button for container nodes
    if (node.isContainer() && node.hasChildren()) {
      const expandButton = document.createElement('button');
      expandButton.className = `tree-expand-button ${node.isExpanded ? 'expanded' : 'collapsed'}`;
      expandButton.innerHTML = node.isExpanded ? '▼' : '▶';
      expandButton.setAttribute('aria-label', node.isExpanded ? 'Collapse' : 'Expand');
      expandButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleNode(node, nodeElement);
      });
      nodeContent.appendChild(expandButton);
    } else {
      // Add spacer for leaf nodes to align content
      const spacer = document.createElement('span');
      spacer.className = 'tree-expand-spacer';
      nodeContent.appendChild(spacer);
    }

    // Add the key (property name)
    const keyElement = document.createElement('span');
    keyElement.className = 'tree-key';

    if (node.level === 0) {
      keyElement.textContent = node.key;
    } else if (node.key.startsWith('[') && node.key.endsWith(']')) {
      // Array index
      keyElement.textContent = node.key;
      keyElement.classList.add('array-index');
    } else {
      // Object property
      keyElement.textContent = `"${node.key}"`;
      keyElement.classList.add('object-key');
    }

    nodeContent.appendChild(keyElement);

    // Add colon separator for non-root nodes
    if (node.level > 0) {
      const separator = document.createElement('span');
      separator.className = 'tree-separator';
      separator.textContent = ': ';
      nodeContent.appendChild(separator);
    }

    // Add the value
    const valueElement = document.createElement('span');
    valueElement.className = `tree-value tree-value-${node.type}`;
    valueElement.textContent = node.getDisplayValue();

    // Add click handler for value selection
    valueElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectNode(node, nodeElement);
    });

    nodeContent.appendChild(valueElement);

    // Add node content to node element
    nodeElement.appendChild(nodeContent);

    // Add children container
    if (node.hasChildren()) {
      const childrenContainer = document.createElement('div');
      childrenContainer.className = 'tree-children';
      childrenContainer.style.display = node.isExpanded ? 'block' : 'none';

      // Render child nodes
      node.children.forEach(childNode => {
        this.renderNode(childNode, childrenContainer);
      });

      nodeElement.appendChild(childrenContainer);
    }

    parentElement.appendChild(nodeElement);
  }

  /**
   * Toggle the expanded state of a node
   * @param {TreeNode} node - The node to toggle
   * @param {HTMLElement} nodeElement - The DOM element for the node
   */
  toggleNode(node, nodeElement) {
    node.toggleExpanded();

    const expandButton = nodeElement.querySelector('.tree-expand-button');
    const childrenContainer = nodeElement.querySelector('.tree-children');

    if (expandButton) {
      expandButton.innerHTML = node.isExpanded ? '▼' : '▶';
      expandButton.className = `tree-expand-button ${node.isExpanded ? 'expanded' : 'collapsed'}`;
      expandButton.setAttribute('aria-label', node.isExpanded ? 'Collapse' : 'Expand');
    }

    if (childrenContainer) {
      childrenContainer.style.display = node.isExpanded ? 'block' : 'none';
    }

    // Emit custom event for external listeners
    this.container.dispatchEvent(new CustomEvent('nodeToggle', {
      detail: { node, expanded: node.isExpanded }
    }));
  }

  /**
   * Select a node and highlight it
   * @param {TreeNode} node - The node to select
   * @param {HTMLElement} nodeElement - The DOM element for the node
   */
  selectNode(node, nodeElement) {
    // Remove previous selection
    const previousSelected = this.container.querySelector('.tree-node-selected');
    if (previousSelected) {
      previousSelected.classList.remove('tree-node-selected');
    }

    // Add selection to current node
    nodeElement.classList.add('tree-node-selected');
    this.selectedNode = node;

    // Emit custom event for external listeners
    this.container.dispatchEvent(new CustomEvent('nodeSelect', {
      detail: { node, path: node.getPathString(), value: node.value }
    }));
  }

  /**
   * Expand all nodes in the tree
   */
  expandAll() {
    if (!this.treeData) return;

    this.treeData.expand(true);
    this.updateAllNodeStates();

    this.container.dispatchEvent(new CustomEvent('treeExpandAll'));
  }

  /**
   * Collapse all nodes in the tree
   */
  collapseAll() {
    if (!this.treeData) return;

    this.treeData.collapse(true);
    this.updateAllNodeStates();

    this.container.dispatchEvent(new CustomEvent('treeCollapseAll'));
  }

  /**
   * Update all node states in the DOM to match the tree data
   */
  updateAllNodeStates() {
    const updateNodeState = (node) => {
      const nodeElement = this.container.querySelector(`[data-path="${node.getPathString()}"]`);
      if (nodeElement) {
        const expandButton = nodeElement.querySelector('.tree-expand-button');
        const childrenContainer = nodeElement.querySelector('.tree-children');

        if (expandButton) {
          expandButton.innerHTML = node.isExpanded ? '▼' : '▶';
          expandButton.className = `tree-expand-button ${node.isExpanded ? 'expanded' : 'collapsed'}`;
          expandButton.setAttribute('aria-label', node.isExpanded ? 'Collapse' : 'Expand');
        }

        if (childrenContainer) {
          childrenContainer.style.display = node.isExpanded ? 'block' : 'none';
        }
      }

      // Recursively update children
      node.children.forEach(child => updateNodeState(child));
    };

    updateNodeState(this.treeData);
  }

  /**
   * Expand nodes up to a specific level
   * @param {number} level - The maximum level to expand to
   */
  expandToLevel(level) {
    if (!this.treeData) return;

    JSONTreeParser.expandToLevel(this.treeData, level);
    this.updateAllNodeStates();

    this.container.dispatchEvent(new CustomEvent('treeExpandToLevel', {
      detail: { level }
    }));
  }

  /**
   * Get the currently selected node
   * @returns {TreeNode|null}
   */
  getSelectedNode() {
    return this.selectedNode;
  }

  /**
   * Clear the current selection
   */
  clearSelection() {
    const selected = this.container.querySelector('.tree-node-selected');
    if (selected) {
      selected.classList.remove('tree-node-selected');
    }
    this.selectedNode = null;
  }

  /**
   * Find and highlight a node by its path
   * @param {string[]} path - The path to the node
   */
  findAndSelectByPath(path) {
    if (!this.treeData) return;

    const node = JSONTreeParser.findNodeByPath(this.treeData, path);
    if (node) {
      const nodeElement = this.container.querySelector(`[data-path="${node.getPathString()}"]`);
      if (nodeElement) {
        this.selectNode(node, nodeElement);
        // Only scroll if scrollIntoView is available (not in test environment)
        if (typeof nodeElement.scrollIntoView === 'function') {
          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }

  /**
   * Get statistics about the current tree
   * @returns {Object} Tree statistics
   */
  getTreeStats() {
    if (!this.treeData) {
      return { totalNodes: 0, leafNodes: 0, maxDepth: 0 };
    }

    const leafNodes = JSONTreeParser.getLeafNodes(this.treeData);
    let maxDepth = 0;
    let totalNodes = 0;

    function countNodes(node) {
      totalNodes++;
      maxDepth = Math.max(maxDepth, node.level);
      node.children.forEach(child => countNodes(child));
    }

    countNodes(this.treeData);

    return {
      totalNodes,
      leafNodes: leafNodes.length,
      maxDepth
    };
  }

  /**
   * Setup keyboard navigation for the tree viewer
   */
  setupKeyboardNavigation() {
    // Make the container focusable
    this.container.setAttribute('tabindex', '0');
    this.container.setAttribute('role', 'tree');
    this.container.setAttribute('aria-label', 'JSON Tree Structure');

    // Add keyboard event listener
    this.container.addEventListener('keydown', this.handleKeydown.bind(this));

    // Add focus/blur handlers
    this.container.addEventListener('focus', this.handleFocus.bind(this));
    this.container.addEventListener('blur', this.handleBlur.bind(this));
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    if (!this.keyboardNavigationEnabled || !this.treeData) {
      return;
    }

    const { key, ctrlKey, metaKey, shiftKey } = event;
    let handled = false;

    switch (key) {
      case 'ArrowDown':
        this.navigateDown();
        handled = true;
        break;
      case 'ArrowUp':
        this.navigateUp();
        handled = true;
        break;
      case 'ArrowRight':
        this.navigateRight();
        handled = true;
        break;
      case 'ArrowLeft':
        this.navigateLeft();
        handled = true;
        break;
      case 'Enter':
      case ' ':
        this.activateCurrentNode();
        handled = true;
        break;
      case 'Home':
        this.navigateToFirst();
        handled = true;
        break;
      case 'End':
        this.navigateToLast();
        handled = true;
        break;
      case '*':
        if (this.focusedNode) {
          this.expandNodeAndChildren(this.focusedNode);
          handled = true;
        }
        break;
      case '+':
        if (this.focusedNode && this.focusedNode.isContainer() && !this.focusedNode.isExpanded) {
          this.toggleNodeExpansion(this.focusedNode);
          handled = true;
        }
        break;
      case '-':
        if (this.focusedNode && this.focusedNode.isContainer() && this.focusedNode.isExpanded) {
          this.toggleNodeExpansion(this.focusedNode);
          handled = true;
        }
        break;
      case '?':
        // Show keyboard help modal
        this.showKeyboardHelp();
        handled = true;
        break;
    }

    // Handle global shortcuts
    if (ctrlKey || metaKey) {
      switch (key) {
        case 'e':
          this.expandAll();
          handled = true;
          break;
        case 'c':
          if (shiftKey) {
            this.collapseAll();
            handled = true;
          }
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          const level = parseInt(key);
          this.expandToLevel(level);
          handled = true;
          break;
      }
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Handle focus event
   */
  handleFocus() {
    if (!this.focusedNode && this.treeData) {
      this.setFocusedNode(this.treeData);
    }
    this.container.classList.add('tree-focused');
  }

  /**
   * Handle blur event
   */
  handleBlur() {
    this.container.classList.remove('tree-focused');
  }

  /**
   * Navigate to the next visible node
   */
  navigateDown() {
    if (!this.focusedNode) {
      this.setFocusedNode(this.treeData);
      return;
    }

    const nextNode = this.getNextVisibleNode(this.focusedNode);
    if (nextNode) {
      this.setFocusedNode(nextNode);
    }
  }

  /**
   * Navigate to the previous visible node
   */
  navigateUp() {
    if (!this.focusedNode) {
      this.setFocusedNode(this.treeData);
      return;
    }

    const prevNode = this.getPreviousVisibleNode(this.focusedNode);
    if (prevNode) {
      this.setFocusedNode(prevNode);
    }
  }

  /**
   * Navigate right (expand node or go to first child)
   */
  navigateRight() {
    if (!this.focusedNode) return;

    if (this.focusedNode.isContainer()) {
      if (!this.focusedNode.isExpanded) {
        this.toggleNodeExpansion(this.focusedNode);
      } else if (this.focusedNode.hasChildren()) {
        this.setFocusedNode(this.focusedNode.children[0]);
      }
    }
  }

  /**
   * Navigate left (collapse node or go to parent)
   */
  navigateLeft() {
    if (!this.focusedNode) return;

    if (this.focusedNode.isContainer() && this.focusedNode.isExpanded) {
      this.toggleNodeExpansion(this.focusedNode);
    } else {
      // Find parent by path
      const parentPath = this.focusedNode.path.slice(0, -1);
      if (parentPath.length > 0) {
        const parent = this.findNodeByPath(parentPath);
        if (parent) {
          this.setFocusedNode(parent);
        }
      }
    }
  }

  /**
   * Activate the current node (select or toggle)
   */
  activateCurrentNode() {
    if (!this.focusedNode) return;

    if (this.focusedNode.isContainer()) {
      this.toggleNodeExpansion(this.focusedNode);
    } else {
      this.selectNodeByReference(this.focusedNode);
    }
  }

  /**
   * Navigate to the first node
   */
  navigateToFirst() {
    if (this.treeData) {
      this.setFocusedNode(this.treeData);
    }
  }

  /**
   * Navigate to the last visible node
   */
  navigateToLast() {
    const lastNode = this.getLastVisibleNode();
    if (lastNode) {
      this.setFocusedNode(lastNode);
    }
  }

  /**
   * Get the next visible node in the tree
   * @param {TreeNode} node - Current node
   * @returns {TreeNode|null}
   */
  getNextVisibleNode(node) {
    // If node is expanded and has children, return first child
    if (node.isExpanded && node.hasChildren()) {
      return node.children[0];
    }

    // Find next sibling or ancestor's next sibling
    let current = node;
    while (current) {
      if (current.parent) {
        const siblings = current.parent.children;
        const currentIndex = siblings.indexOf(current);
        if (currentIndex < siblings.length - 1) {
          return siblings[currentIndex + 1];
        }
        current = current.parent;
      } else {
        break;
      }
    }

    return null;
  }

  /**
   * Get the previous visible node in the tree
   * @param {TreeNode} node - Current node
   * @returns {TreeNode|null}
   */
  getPreviousVisibleNode(node) {
    const allVisibleNodes = this.getAllVisibleNodes();
    const currentIndex = allVisibleNodes.indexOf(node);

    if (currentIndex > 0) {
      return allVisibleNodes[currentIndex - 1];
    }

    return null;
  }

  /**
   * Get the last visible node in the tree
   * @returns {TreeNode|null}
   */
  getLastVisibleNode() {
    if (!this.treeData) return null;

    let current = this.treeData;
    while (current.isExpanded && current.hasChildren()) {
      current = current.children[current.children.length - 1];
    }
    return current;
  }

  /**
   * Set the focused node and update visual focus
   * @param {TreeNode} node - Node to focus
   */
  setFocusedNode(node) {
    // Remove previous focus
    if (this.focusedNode) {
      const prevElement = this.container.querySelector(`[data-path="${this.focusedNode.getPathString()}"]`);
      if (prevElement) {
        prevElement.classList.remove('tree-node-focused');
        prevElement.removeAttribute('aria-selected');
      }
    }

    this.focusedNode = node;

    if (node) {
      const nodeElement = this.container.querySelector(`[data-path="${node.getPathString()}"]`);
      if (nodeElement) {
        nodeElement.classList.add('tree-node-focused');
        nodeElement.setAttribute('aria-selected', 'true');

        // Scroll into view if needed
        if (typeof nodeElement.scrollIntoView === 'function') {
          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }

    // Emit focus change event
    this.container.dispatchEvent(new CustomEvent('nodeFocus', {
      detail: { node, path: node ? node.getPathString() : null }
    }));
  }

  /**
   * Toggle node expansion and update UI
   * @param {TreeNode} node - Node to toggle
   */
  toggleNodeExpansion(node) {
    const nodeElement = this.container.querySelector(`[data-path="${node.getPathString()}"]`);
    if (nodeElement) {
      this.toggleNode(node, nodeElement);
    }
  }

  /**
   * Select a node by reference
   * @param {TreeNode} node - Node to select
   */
  selectNodeByReference(node) {
    const nodeElement = this.container.querySelector(`[data-path="${node.getPathString()}"]`);
    if (nodeElement) {
      this.selectNode(node, nodeElement);
    }
  }

  /**
   * Expand a node and all its children
   * @param {TreeNode} node - Node to expand recursively
   */
  expandNodeAndChildren(node) {
    node.expand(true);
    this.updateAllNodeStates();

    this.container.dispatchEvent(new CustomEvent('nodeExpandRecursive', {
      detail: { node, path: node.getPathString() }
    }));
  }

  /**
   * Enable or disable keyboard navigation
   * @param {boolean} enabled - Whether to enable keyboard navigation
   */
  setKeyboardNavigationEnabled(enabled) {
    this.keyboardNavigationEnabled = enabled;
    if (enabled) {
      this.container.setAttribute('tabindex', '0');
    } else {
      this.container.removeAttribute('tabindex');
    }
  }

  /**
   * Get the currently focused node
   * @returns {TreeNode|null}
   */
  getFocusedNode() {
    return this.focusedNode;
  }

  /**
   * Clear keyboard focus
   */
  clearFocus() {
    if (this.focusedNode) {
      const nodeElement = this.container.querySelector(`[data-path="${this.focusedNode.getPathString()}"]`);
      if (nodeElement) {
        nodeElement.classList.remove('tree-node-focused');
        nodeElement.removeAttribute('aria-selected');
      }
      this.focusedNode = null;
    }
  }

  /**
   * Get all visible nodes in the tree in order
   * @returns {TreeNode[]}
   */
  getAllVisibleNodes() {
    if (!this.treeData) return [];

    const visibleNodes = [];

    const traverse = (node) => {
      visibleNodes.push(node);
      if (node.isExpanded && node.hasChildren()) {
        node.children.forEach(child => traverse(child));
      }
    };

    traverse(this.treeData);
    return visibleNodes;
  }

  /**
   * Find a node by its path
   * @param {string[]} path - The path to the node
   * @returns {TreeNode|null}
   */
  findNodeByPath(path) {
    if (!this.treeData) return null;

    let current = this.treeData;

    // Skip the root path element
    for (let i = 1; i < path.length; i++) {
      const pathElement = path[i];
      const child = current.children.find(c => c.key === pathElement);
      if (!child) return null;
      current = child;
    }

    return current;
  }

  /**
   * Show keyboard help modal
   * This method dispatches an event that the main application can listen to
   */
  showKeyboardHelp() {
    // Dispatch a custom event to show help
    const event = new CustomEvent('showKeyboardHelp', {
      bubbles: true,
      detail: { source: 'treeViewer' }
    });
    this.container.dispatchEvent(event);
  }
}